import { useEffect, useState } from 'react'

import type { Query } from 'common/query'
import type { ListResult, SortOrder } from 'common/api/v1/types'

export type PaginatedDataFetchingFilter<
    BoolFilter extends { [filterName: string]: boolean },
    FieldFilter extends { [filterName: string]: string } = Record<string, never>,
> = {
    searchString?: string
    boolFilters?: BoolFilter
    fieldFilters?: FieldFilter
}
interface PaginatedDataFetchingProps<
    Item,
    SortBy extends string,
    BoolFilter extends { [filterName: string]: boolean },
    FieldFilter extends { [filterName: string]: string } = Record<string, never>,
> {
    api: (
        query: Query<PaginatedDataFetchingFilter<BoolFilter, FieldFilter>, SortOrder<SortBy>>
    ) => Promise<ListResult<Item>>
    currentPage: number
    rowsPerPage: number
    searchString?: PaginatedDataFetchingFilter<BoolFilter, FieldFilter>['searchString']
    boolFilters?: PaginatedDataFetchingFilter<BoolFilter, FieldFilter>['boolFilters']
    fieldFilters?: PaginatedDataFetchingFilter<BoolFilter, FieldFilter>['fieldFilters']
    sortOrder?: SortOrder<SortBy>
}
export function usePaginatedDataFetching<
    Item,
    SortBy extends string,
    BoolFilter extends { [filterName: string]: boolean },
    FieldFilter extends { [filterName: string]: string } = Record<string, never>,
>({
    api,
    currentPage,
    rowsPerPage,
    searchString,
    boolFilters,
    fieldFilters,
    sortOrder,
}: PaginatedDataFetchingProps<Item, SortBy, BoolFilter, FieldFilter>) {
    const [listResult, setListResult] = useState<ListResult<Item>>({ items: [], total: 0 })
    const [isFetching, setIsFetching] = useState(true)
    const [forceRefresh, setForceRefresh] = useState(false)
    const refreshData = () => setForceRefresh(!forceRefresh)
    const boolFiltersKey = JSON.stringify(boolFilters ?? {})
    const fieldFiltersKey = JSON.stringify(fieldFilters ?? {})

    useEffect(() => {
        async function doFetch() {
            setIsFetching(true)

            const query: Query<PaginatedDataFetchingFilter<BoolFilter, FieldFilter>, SortOrder<SortBy>> = {
                filter: { searchString, boolFilters, fieldFilters },
                skip: currentPage * rowsPerPage,
                limit: rowsPerPage,
                order: sortOrder,
            }
            try {
                const response = await api(query)
                // Fixes issue where listResult.total sometimes is suffixed with a character, e.g. '8n'
                const total = Number.parseInt(response.total.toString())
                setListResult({ ...response, total })
            } catch (error) {
                // Swallow the error and do nothing - it is the responsibility of the client (i.e. the function provided as 'api' parameter) to handle the errors,
                // e.g. by showing an appropriate UI, performing a retry followed by a call to refreshData().
                // oxlint-disable-next-line
                console.log(`usePaginatedDataFetching doFetchError: `, error)
            }
            setIsFetching(false)
        }
        void doFetch()
    }, [
        currentPage,
        rowsPerPage,
        sortOrder?.field,
        sortOrder?.descending,
        boolFiltersKey,
        fieldFiltersKey,
        searchString,
        forceRefresh,
    ])

    return { ...listResult, isFetching, refreshData }
}
